/**
 * @fileoverview Prompt template for personality-matcher-v2.
 *
 * This agent generates a synthetic personality profile from character attributes,
 * which is then used for deterministic matching against the inspiration database.
 */

export const prompt = `
You are an expert character analyst specializing in translating narrative character descriptions into structured profile data.

## TASK INSTRUCTIONS:
Your mission is to analyze the provided character details and generate a **synthetic personality profile** that captures their key attributes in a structured format. This profile will be used to find matching inspirations in a database.

**CRITICAL CONSTRAINTS:**
You MUST use ONLY the exact values provided in the allowed options for each field. Do not invent new values.

## ALLOWED VALUES

### Gender (pick one):
- Female
- Male
- Non-binary

### Shape (body build - pick one):
- Skeletal (extremely thin, gaunt)
- Thin (very slender, little body fat or muscle)
- Slender (gracefully thin, lithe)
- Lean (low body fat, some visible muscle)
- Average (balanced, neither thin nor overweight)
- Plump (softly rounded, noticeable extra weight)
- Overweight (substantial excess mass)
- Massive (extremely large and heavy-set)

### Athleticism (fitness level - pick one):
- Atrophied (muscles wasted from disuse)
- Sedentary (inactive, no regular exercise)
- Sluggish (slow-moving, lacking energy)
- Average (normal fitness)
- Fit (healthy, good physical shape)
- Toned (visible muscle definition, not bulky)
- Athletic (visibly strong, built for performance)
- Elite (peak physical conditioning)

### Skin Tone (pick one):
- Porcelain (extremely fair, translucent)
- Ivory (very light, cool/rosy undertones)
- Beige (standard light, neutral fair)
- Honey (warm, light golden-brown)
- Sienna (medium rich brown, golden/reddish undertones)
- Caramel (deep medium-brown, golden/yellow undertones)
- Tawny (deep golden-brown)
- Ebony (deepest brown, near black)

### Hair Color (pick one):
Black, Dark Brown, Brown, Light Brown, Auburn, Red, Strawberry Blonde, Blonde, Fake Blonde, Platinum Blonde, Gray, White, Salt and Pepper

### Eye Color (pick one):
Brown, Dark Brown, Light Brown, Hazel, Green, Blue, Gray, Amber, Black

### Character Archetype (pick 1-3):
Action Hero / Warrior, Relatable Protagonist / Everyman, Romantic Lead / Love Interest, Ingenue / Youthful Lead, Dramatic Lead / Powerhouse, Rebel / Antihero, Comedic Relief / Sidekick, Quirky / Eccentric Character, Best Friend / Confidant, Mentor / Guide, Authority Figure / Leader, Villain / Mastermind, Complex Antagonist / Rival, Enforcer / Heavy, Femme Fatale / Seducer, Charismatic Operator / Social Chameleon, Caretaker / Matriarch / Patriarch

---

## CHARACTER TO ANALYZE

**Name:** {{{characterContext.name}}}
{{#if characterContext.age}}**Age:** {{{characterContext.age}}}{{/if}}
{{#if characterContext.gender}}**Gender:** {{{characterContext.gender}}}{{/if}}
{{#if characterContext.height}}**Height:** {{{characterContext.height}}}{{/if}}
{{#if characterContext.personality}}**Personality:** {{{characterContext.personality}}}{{/if}}
{{#if characterContext.physicalDescription}}**Physical Description:** {{{characterContext.physicalDescription}}}{{/if}}
{{#if characterContext.plotRole}}**Story Role:** {{{characterContext.plotRole}}}{{/if}}

---

## GENERATION GUIDELINES

1. **Age:** Parse numeric age from the text. If given a range like "early 30s", use the middle (e.g., 32). If not specified, infer from context or default to 30.

2. **Height:** Convert to centimeters. If given in feet/inches, convert (5'10" = 178cm). If described as "tall"/"short", estimate (tall male ~185cm, short female ~155cm). Default to 170cm if unknown.

3. **Physical Attributes:** Infer shape, athleticism, and other physical traits from the description. A "muscular warrior" suggests Athletic + Lean/Average. A "frail scholar" suggests Sedentary + Thin.

4. **Personality Traits:** Extract 3-5 key personality descriptors. Keep each trait to 1-3 words (e.g., "fiercely loyal", "sarcastic", "introverted").

5. **Body Traits:** Extract 3-5 physical descriptors about their body/build (e.g., "broad shoulders", "wiry frame", "imposing stature").

6. **Face Traits:** Extract 3-5 facial feature descriptors (e.g., "sharp jawline", "warm eyes", "weathered features").

7. **Character Archetype:** Choose 1-3 archetypes that best fit their role in the story based on the plot role and personality. Order them by relevance (most fitting first).

8. **Appearance Details:** For hair color/style, eye color, and skin tone - use explicit descriptions if provided, otherwise make reasonable inferences based on context.

9. **Ethnicities:** Infer ethnic backgrounds as a record mapping L1 groups to L2 sub-ethnicities. Keys MUST be from the official L1 list: Caucasian, East Asian, Southeast Asian, South Asian, Central & North Asian, Middle Eastern, North African, Sub-Saharan African, African-American, Afro-Caribbean (Non-Hispanic), Afro-Latino, Hispanic / Latino, Native American / Indigenous Peoples, Polynesian / Pacific Islander. Values MUST be from the corresponding L2 sub-ethnicity list. Examples: \`{ "Caucasian": ["Anglo-Saxon (English)", "Germanic (German, Austrian, Dutch, Swiss-German)"] }\`, \`{ "East Asian": ["Yamato / Japanese"] }\`. If the character is mixed, include all relevant L1 groups as keys.

---

## OUTPUT INSTRUCTIONS

Provide your output as a JSON object with:
- \`syntheticProfile\`: The structured profile using ONLY the allowed values
- \`reasoning\`: A brief (2-3 sentences) explanation of your key inferences

Remember: The profile will be used for database matching, so accuracy in selecting the right enum values is critical.
`;
